import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Send, Plus } from 'lucide-react';
import api from '@/services/apiClient';
import { getSocket } from '@/services/socket';
import useAuthStore from '@/store/authStore';
import PageHeader from '@/components/ui/PageHeader';
import Card from '@/components/ui/Card';
import Avatar from '@/components/ui/Avatar';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import Modal from '@/components/ui/Modal';
import { fmtDate } from '@/lib/enums';
import styles from './Chat.module.css';

function fmtTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const today = new Date();
  const sameDay = dt.toDateString() === today.toDateString();
  return sameDay
    ? dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : fmtDate(dt);
}

export default function TrainerChat() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);

  const [conversations, setConversations] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const [picking, setPicking] = useState(false);
  const [students, setStudents] = useState([]);
  const [starting, setStarting] = useState(null);

  const bottomRef = useRef(null);

  const loadConversations = useCallback(() => {
    return api
      .get('/chat/conversations')
      .then((res) => {
        const list = (res.data || res).conversations || res.data || [];
        setConversations(list);
        return list;
      })
      .catch(() => {
        setConversations([]);
        return [];
      });
  }, []);

  useEffect(() => {
    loadConversations().then((list) => {
      if (list.length) setActiveId((cur) => cur ?? list[0].id);
    });
  }, [loadConversations]);

  const loadMessages = useCallback((id, silent = false) => {
    if (!id) return;
    if (!silent) setLoadingMsgs(true);
    api
      .get(`/chat/conversations/${id}/messages`)
      .then((res) => setMessages((res.data || res).messages || res.data || []))
      .catch(() => { if (!silent) setMessages([]); })
      .finally(() => { if (!silent) setLoadingMsgs(false); });
  }, []);

  // Carrega a thread ao trocar de conversa e entra na sala do socket para
  // receber mensagens em tempo real (sem polling).
  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    loadMessages(activeId);

    const socket = getSocket();
    socket.emit('conversation:join', activeId);

    const onNew = (msg) => {
      if (String(msg.conversationId) !== String(activeId)) return;
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    };
    const onBump = () => loadConversations();
    socket.on('message:new', onNew);
    socket.on('conversation:bump', onBump);

    return () => {
      socket.emit('conversation:leave', activeId);
      socket.off('message:new', onNew);
      socket.off('conversation:bump', onBump);
    };
  }, [activeId, loadMessages, loadConversations]);

  // Auto-scroll para a mensagem mais recente.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, activeId]);

  const send = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || !activeId) return;
    setSending(true);
    try {
      const res = await api.post(`/chat/conversations/${activeId}/messages`, { body });
      const msg = (res.data || res).message;
      setDraft('');
      // Acrescenta de imediato (o eco via socket é deduplicado pelo id).
      if (msg) setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      loadConversations();
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  const openPicker = () => {
    setPicking(true);
    api
      .get('/students')
      .then((res) => setStudents((res.data || res).students || res.data || []))
      .catch(() => setStudents([]));
  };

  const startWith = async (student) => {
    const userId = student.userId || student.user?.id || student.id;
    setStarting(userId);
    try {
      const res = await api.post('/chat/conversations', { userId });
      const conv = (res.data || res).conversation || res.data || res;
      setPicking(false);
      await loadConversations();
      if (conv?.id) setActiveId(conv.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setStarting(null);
    }
  };

  if (conversations === null) {
    return (
      <>
        <PageHeader kicker="Movez · Personal" title={t('nav.chat')} />
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}><Spinner /></div>
      </>
    );
  }

  return (
    <>
      <PageHeader kicker="Movez · Personal" title={t('nav.chat')}>
        <Button icon={Plus} onClick={openPicker}>{t('chat.newConversation')}</Button>
      </PageHeader>

      {conversations.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title={t('chat.noConversations')}
          description={t('chat.noConversationsDesc')}
          action={<Button icon={Plus} onClick={openPicker}>{t('chat.startConversation')}</Button>}
        />
      ) : (
        <Card padding="none" className={styles.shell}>
          <div className={styles.list}>
            {conversations.map((c) => {
              const o = c.otherUser || {};
              const last = c.lastMessage?.body || c.lastMessage || '';
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`${styles.convItem} ${c.id === activeId ? styles.convActive : ''}`}
                  onClick={() => setActiveId(c.id)}
                >
                  <Avatar name={o.name} src={o.avatar} size={40} />
                  <div className={styles.convBody}>
                    <div className={styles.convTop}>
                      <span className={styles.convName}>{o.name || t('chat.student')}</span>
                      <span className={styles.convTime}>{fmtTime(c.lastMessageAt)}</span>
                    </div>
                    {last && (
                      <span className={styles.convLast}>{typeof last === 'string' ? last : ''}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className={styles.thread}>
            {!activeId ? (
              <div className={styles.threadEmpty}>
                <EmptyState icon={MessageSquare} title={t('chat.selectConversation')} />
              </div>
            ) : (
              <>
                <div className={styles.messages}>
                  {loadingMsgs ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}><Spinner /></div>
                  ) : messages.length === 0 ? (
                    <div className={styles.threadEmpty}>
                      <EmptyState icon={MessageSquare} title={t('chat.noMessages')} description={t('chat.noMessagesDesc')} />
                    </div>
                  ) : (
                    messages.map((m) => {
                      const mine = String(m.senderId) === String(me?.id);
                      return (
                        <div key={m.id} className={`${styles.bubbleRow} ${mine ? styles.mine : styles.theirs}`}>
                          <div className={styles.bubble}>
                            <span className={styles.bubbleText}>{m.body}</span>
                            <span className={styles.bubbleTime}>{fmtTime(m.createdAt)}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={bottomRef} />
                </div>

                <form className={styles.composer} onSubmit={send}>
                  <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={t('chat.messagePlaceholder')}
                    style={{ flex: 1 }}
                  />
                  <Button type="submit" icon={Send} loading={sending} disabled={!draft.trim()} />
                </form>
              </>
            )}
          </div>
        </Card>
      )}

      <Modal open={picking} onClose={() => setPicking(false)} kicker={t('chat.modalKicker')} title={t('chat.startConversation')} size="sm">
        {students.length === 0 ? (
          <EmptyState title={t('chat.noStudents')} description={t('chat.noStudentsDesc')} />
        ) : (
          <div className={styles.pickList}>
            {students.map((s) => (
              <button
                key={s.id}
                type="button"
                className={styles.pickItem}
                onClick={() => startWith(s)}
                disabled={starting != null}
              >
                <Avatar name={s.name} src={s.avatar} size={36} />
                <span className={styles.pickName}>{s.name}</span>
                {starting === (s.userId || s.user?.id || s.id) && <Spinner size={16} />}
              </button>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
